import { MINUTES_PER_HOUR } from "@shared-kernel/core/constants/time.constants";
import type { Nullable } from "@shared-kernel/core/types/nullable";
import { timeStringToMinutes } from "./time-string.utils";
import type {
  DateScheduleConfig,
  DayTemplate,
  HalfDay,
  HalfDayLockPredicate,
  HalfDayScheduleConfig,
  TimeSlotOption,
  TrainingInterval,
} from "./training-schedule-form.types";
import {
  AFTERNOON_TIME_SLOTS,
  DEFAULT_DAY_TEMPLATE,
  HALF_DAY_SCHEDULE_ERRORS,
  MORNING_END_TIME_SLOTS,
  MORNING_START_TIME_SLOTS,
} from "./training-schedule-form.constants";

/**
 * Convert time string to Date object for a given date
 */
export const timeStringToDate = (date: Date, timeString: string): Date => {
  const [hours = 0, minutes = 0] = timeString.split(":").map(Number);
  const result = new Date(date);
  result.setHours(hours, minutes, 0, 0);
  return result;
};

/**
 * Convert DateScheduleConfig[] to TrainingInterval[] (flat array)
 */
export const dateSchedulesToIntervals = (schedules: DateScheduleConfig[]): TrainingInterval[] => {
  const intervals: TrainingInterval[] = [];

  for (const schedule of schedules) {
    if (schedule.morning.enabled) {
      intervals.push({
        start: timeStringToDate(schedule.date, schedule.morning.start),
        end: timeStringToDate(schedule.date, schedule.morning.end),
      });
    }
    if (schedule.afternoon.enabled) {
      intervals.push({
        start: timeStringToDate(schedule.date, schedule.afternoon.start),
        end: timeStringToDate(schedule.date, schedule.afternoon.end),
      });
    }
  }

  // Sort by start date
  return intervals.sort((a, b) => a.start.getTime() - b.start.getTime());
};

/**
 * Convert TrainingInterval[] back to DateScheduleConfig[] (for initializing from saved data)
 */
export const intervalsToDateSchedules = (intervals: TrainingInterval[]): DateScheduleConfig[] => {
  const dateMap = new Map<string, DateScheduleConfig>();

  for (const interval of intervals) {
    const dateKey = interval.start.toISOString().split("T")[0] ?? "";
    const hours = interval.start.getHours();
    const isMorning = hours < 12;

    if (!dateMap.has(dateKey)) {
      const dateOnly = new Date(interval.start);
      dateOnly.setHours(0, 0, 0, 0);
      dateMap.set(dateKey, {
        date: dateOnly,
        morning: { ...DEFAULT_DAY_TEMPLATE.morning, enabled: false },
        afternoon: { ...DEFAULT_DAY_TEMPLATE.afternoon, enabled: false },
      });
    }

    const config = dateMap.get(dateKey);
    if (!config) continue;

    const formatTime = (d: Date) => `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;

    if (isMorning) {
      config.morning = {
        enabled: true,
        start: formatTime(interval.start),
        end: formatTime(interval.end),
      };
    } else {
      config.afternoon = {
        enabled: true,
        start: formatTime(interval.start),
        end: formatTime(interval.end),
      };
    }
  }

  return Array.from(dateMap.values()).sort((a, b) => a.date.getTime() - b.date.getTime());
};

/**
 * Create a new DateScheduleConfig from the default day template (afternoon enabled by default)
 */
export const createDefaultDateSchedule = (date: Date): DateScheduleConfig => ({
  date,
  morning: { ...DEFAULT_DAY_TEMPLATE.morning },
  afternoon: { ...DEFAULT_DAY_TEMPLATE.afternoon },
});

/**
 * Count total half-days from intervals
 */
export const computeHalfDaysFromIntervals = (intervals: TrainingInterval[]): number => {
  return intervals.length; // Each interval = 1 half-day
};

/**
 * Get valid end time options based on selected start time
 * End time must be after start time
 */
export const getValidEndTimeOptions = (startTime: string, allOptions: TimeSlotOption[]): TimeSlotOption[] => {
  const startMinutes = timeStringToMinutes(startTime);

  return allOptions.filter(option => timeStringToMinutes(option.value) > startMinutes);
};

/**
 * Get valid start time options based on selected end time
 * Start time must be before end time
 */
export const getValidStartTimeOptions = (endTime: string, allOptions: TimeSlotOption[]): TimeSlotOption[] => {
  const endMinutes = timeStringToMinutes(endTime);

  return allOptions.filter(option => timeStringToMinutes(option.value) < endMinutes);
};

/**
 * Time slot options of both half-days, filtered by the times currently selected.
 * Shared by every place rendering a morning/afternoon pair so the bounds stay identical.
 */
export const getDayTimeOptions = ({ morning, afternoon }: DayTemplate) => ({
  morning: {
    start: getValidStartTimeOptions(morning.end, MORNING_START_TIME_SLOTS),
    end: getValidEndTimeOptions(morning.start, MORNING_END_TIME_SLOTS),
  },
  afternoon: {
    start: getValidStartTimeOptions(afternoon.end, AFTERNOON_TIME_SLOTS),
    end: getValidEndTimeOptions(afternoon.start, AFTERNOON_TIME_SLOTS),
  },
});

/**
 * Calculate hours from a time interval (start and end time strings like "09:00", "12:00")
 * Returns the duration in hours (can be decimal, e.g., 3.5)
 */
export const calculateHoursFromInterval = (start: string, end: string): number => {
  return (timeStringToMinutes(end) - timeStringToMinutes(start)) / MINUTES_PER_HOUR;
};

/**
 * Calculate total hours for a single day schedule
 */
export const calculateDayHours = ({ morning, afternoon }: DayTemplate): number => {
  let total = 0;
  if (morning.enabled) {
    total += calculateHoursFromInterval(morning.start, morning.end);
  }
  if (afternoon.enabled) {
    total += calculateHoursFromInterval(afternoon.start, afternoon.end);
  }
  return total;
};

/**
 * Calculate total hours for all schedules
 */
export const calculateTotalHours = (schedules: DateScheduleConfig[]): number => {
  return schedules.reduce((total, schedule) => total + calculateDayHours(schedule), 0);
};

/**
 * Format hours as "X h" or "X h Y min" (e.g., 7.5 -> "7 h 30 min", 3 -> "3 h")
 */
export const formatHoursDisplay = (hours: number): string => {
  const wholeHours = Math.floor(hours);
  const minutes = Math.round((hours - wholeHours) * MINUTES_PER_HOUR);

  if (minutes === 0) {
    return `${wholeHours} h`;
  }

  return `${wholeHours} h ${minutes} min`;
};

// Half-days as the validation schema sees them: sub-fields may still be missing when a rule runs
type PartialDayTemplate = { [K in HalfDay]?: Partial<HalfDayScheduleConfig> };

/**
 * Whether at least one half-day of the day is enabled
 */
export const hasAtLeastOneHalfDay = ({ morning, afternoon }: PartialDayTemplate): boolean => {
  return Boolean(morning?.enabled || afternoon?.enabled);
};

/**
 * Whether the morning and afternoon intervals of a day overlap
 */
export const hasHalfDayOverlap = ({ morning, afternoon }: PartialDayTemplate): boolean => {
  if (!morning?.enabled || !afternoon?.enabled) return false;
  if (!morning.end || !afternoon.start) return false;

  return timeStringToMinutes(morning.end) > timeStringToMinutes(afternoon.start);
};

/**
 * The half-day rules a day must satisfy, as a single message (null when valid).
 * Same rules and same wording as the training schedules validation schema.
 */
export const getDayScheduleError = (day: DayTemplate): Nullable<string> => {
  if (!hasAtLeastOneHalfDay(day)) return HALF_DAY_SCHEDULE_ERRORS.noHalfDayEnabled;
  if (hasHalfDayOverlap(day)) return HALF_DAY_SCHEDULE_ERRORS.overlap;

  return null;
};

/**
 * Apply a day template (enabled state + times) to a single date schedule.
 * Extra half-day data (speakers) is preserved, locked half-days are left untouched.
 */
export const applyDayTemplate = (
  schedule: DateScheduleConfig,
  template: DayTemplate,
  isHalfDayLocked?: HalfDayLockPredicate,
): DateScheduleConfig => {
  const applyToHalfDay = (halfDay: HalfDay) => {
    if (isHalfDayLocked?.(schedule.date, halfDay)) return schedule[halfDay];
    return { ...schedule[halfDay], ...template[halfDay] };
  };

  return { ...schedule, morning: applyToHalfDay("morning"), afternoon: applyToHalfDay("afternoon") };
};
